import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { FeatherShieldCheck } from "@subframe/core";
import { format } from "date-fns";

import {
  ROLE_DISPLAY,
  STATUS_BADGE_VARIANT,
  STATUS_DISPLAY,
} from "@/components/compliance/compliance-constants";
import { CategoryPill } from "@/components/ui/category-pill";
import { EntityIcon } from "@/components/ui/entity-icon";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model/complianceTaskInstanceResponseDto";
import { asCategoryType, categoryColor } from "@/lib/category-display";
import {
  type ComplianceInstanceTab,
  type ComplianceSortColumn,
  dateLabel,
  dueRelativeLabel,
  frequencyLabel,
  primaryCategory,
  type SortDir,
} from "@/lib/compliance-manager";

interface ComplianceManagerTableProps {
  items: ComplianceTaskInstanceResponseDto[];
  categories: CategoryResponseDto[];
  nurseryName: string;
  tab: ComplianceInstanceTab;
  activeColumn?: ComplianceSortColumn;
  activeDir: SortDir;
  onSort: (column: ComplianceSortColumn) => void;
  onView: (id: string) => void;
}

/**
 * The compliance row's entity glyph, tinted by its primary category. Falls
 * back to a neutral shield when the category slug isn't part of the fixed
 * icon matrix (e.g. an admin-created category).
 */
function ComplianceEntityIcon({
  slug,
  color,
  size = 32,
}: {
  slug?: string;
  color?: string;
  size?: number;
}) {
  const category = asCategoryType(slug);
  if (category) {
    return (
      <EntityIcon entity="compliance-action" category={category} size={size} />
    );
  }
  const tint = categoryColor(slug, color);
  return (
    <span
      className="inline-flex items-center justify-center rounded-md"
      style={{
        width: size,
        height: size,
        backgroundColor: tint ? `${tint}1A` : "#F1F1F1",
        color: tint ?? "#6B7280",
      }}
    >
      <FeatherShieldCheck style={{ width: size * 0.5, height: size * 0.5 }} />
    </span>
  );
}

function responsibleLabel(item: ComplianceTaskInstanceResponseDto): string {
  if (item.assigneeRole) {
    return ROLE_DISPLAY[item.assigneeRole] ?? item.assigneeRole;
  }
  return item.assigneeUserId ? "Specific person" : "—";
}

export function ComplianceManagerTable({
  items,
  categories,
  nurseryName,
  tab,
  activeColumn,
  activeDir,
  onSort,
  onView,
}: ComplianceManagerTableProps) {
  // On the completion tabs a row's own due date is the occurrence that was closed
  // out, which reads as a completion date sitting next to a "Completed" badge
  // (NAU-365). Those tabs show when it was completed and when the series is next
  // due instead; the Open tab keeps the due date, where it's the live one.
  const showCompletionDates = tab !== "open";

  return (
    <Table
      className={TABLE_MIN_WIDTH.lg}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="task"
            label="Task"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="category"
            label="Category"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell>Location</Table.HeaderCell>
          <SortableHeaderCell
            column="responsible"
            label="Responsible"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          {showCompletionDates ? (
            <>
              <SortableHeaderCell
                column="completed"
                label="Completed"
                activeColumn={activeColumn}
                activeDir={activeDir}
                onSort={onSort}
              />
              {/* Series-level, so many rows of one task share a value — sorting by
                  it would be meaningless. */}
              <Table.HeaderCell>Next due</Table.HeaderCell>
            </>
          ) : (
            <SortableHeaderCell
              column="dueDate"
              label="Due date"
              activeColumn={activeColumn}
              activeDir={activeDir}
              onSort={onSort}
            />
          )}
          <SortableHeaderCell
            column="status"
            label="Status"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell variant="aligned-right">Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {items.map((item) => {
        const category = primaryCategory(item, categories);
        const relative = dueRelativeLabel(item.dueDate, item.status);

        return (
          <Table.Row key={item.id} clickable onClick={() => onView(item.id)}>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-3">
                <ComplianceEntityIcon
                  slug={category?.slug}
                  color={category?.color ?? undefined}
                />
                <div className="flex flex-col items-start">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    {item.definitionName ?? "—"}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {frequencyLabel(item.frequency)}
                  </span>
                </div>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {category ? (
                <CategoryPill
                  name={category.name}
                  slug={category.slug}
                  color={category.color ?? undefined}
                />
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {nurseryName}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {responsibleLabel(item)}
              </span>
            </Table.Cell>
            {showCompletionDates ? (
              <>
                <Table.Cell className="h-14 grow shrink-0 basis-0">
                  <span className="text-caption font-caption text-neutral-900">
                    {dateLabel(item.completedAt)}
                  </span>
                </Table.Cell>
                <Table.Cell className="h-14 grow shrink-0 basis-0">
                  <span className="text-caption font-caption text-neutral-900">
                    {dateLabel(item.seriesNextDueDate)}
                  </span>
                </Table.Cell>
              </>
            ) : (
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <div className="flex flex-col items-start">
                  <span className="text-caption font-caption text-neutral-900">
                    {format(new Date(item.dueDate), "d MMM yyyy")}
                  </span>
                  {relative && (
                    <span
                      className={`text-caption font-caption ${
                        relative === "Overdue"
                          ? "text-error-600"
                          : relative === "Due today"
                            ? "text-warning-700"
                            : "text-subtext-color"
                      }`}
                    >
                      {relative}
                    </span>
                  )}
                </div>
              </Table.Cell>
            )}
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <Badge variant={STATUS_BADGE_VARIANT[item.status] ?? "neutral"}>
                {STATUS_DISPLAY[item.status] ?? item.status}
              </Badge>
            </Table.Cell>
            <Table.Cell
              className="h-14 grow shrink-0 basis-0"
              variant="aligned-right"
            >
              <div
                className="flex items-center justify-end"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="neutral-primary"
                  onClick={() => onView(item.id)}
                >
                  View
                </Button>
              </div>
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}
